import { Spin } from "antd";
import { isEqual } from "lodash";
import {
  Box,
  ChevronDown,
  ChevronRight,
  Package,
  Search,
  SearchIcon,
} from "lucide-react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useGetInventoriesInfinite } from "~/services/inventory";
import { capitalize } from "~/utils";
import { InventoryStatusCode, InventoryType } from "~/utils/constants";
import { useDeconsolidateStore } from "./deconsolidate.store";

type Props = {
  goToNextStep: () => void;
};

export const SelectContainer: FC<Props> = ({ goToNextStep }) => {
  const [expandedContainer, setExpandedContainer] = useState<string | null>(
    null
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const observerRef = useRef<HTMLDivElement | null>(null);

  const {
    clientId,
    selectedContainer,
    setSelectedContainer,
    setListContainers,
    listContainers,
  } = useDeconsolidateStore();

  const {
    data: containers,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetInventoriesInfinite({
    itemType: InventoryType.PACKAGE,
    search: searchTerm,
    clientId,
  });

  const allContainers = useMemo(
    () => containers?.pages.flatMap((page) => page.items) ?? [],
    [containers]
  );

  useEffect(() => {
    if (allContainers.length > 0 && !isEqual(listContainers, allContainers)) {
      setListContainers(allContainers);
    }
  }, [allContainers, listContainers, setListContainers]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 }
    );

    const currentRef = observerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const toggleContainerExpansion = (containerId: string) => {
    if (expandedContainer === containerId) {
      setExpandedContainer(null);
    } else {
      setExpandedContainer(containerId);
    }
  };

  const handleFilterClick = () => {
    setSearchTerm(searchInput);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    // Auto-search as user types with debounce
    setSearchTerm(value);
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="p-4 border-b">
        <h2 className="font-medium">Select a Container</h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose a container with pieces you want to remove.
        </p>
      </div>

      <div className="p-4 border-b">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
              placeholder="Search containers..."
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearchTerm(searchInput);
                }
              }}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={16}
            />
          </div>
          <button
            className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-1"
            onClick={handleFilterClick}
          >
            <SearchIcon size={16} />
            <span>Search</span>
          </button>
        </div>
      </div>

      <Spin spinning={isLoading}>
        <div className="divide-y max-h-96 overflow-y-auto">
          {allContainers.length > 0 ? (
            allContainers.map((container) => (
              <div
                key={container.id}
                onClick={() => setSelectedContainer(container)}
              >
                <div
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${
                    selectedContainer?.id === container.id ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start space-x-3">
                      <button
                        className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleContainerExpansion(container.id);
                        }}
                      >
                        {expandedContainer === container.id ? (
                          <ChevronDown size={16} className="text-gray-600" />
                        ) : (
                          <ChevronRight size={16} className="text-gray-600" />
                        )}
                      </button>

                      <div className="flex-1 cursor-pointer">
                        <div className="font-medium">{container.title}</div>
                        <div className="text-sm text-gray-500">
                          {container.inventoryRef}
                        </div>
                        <div className="flex items-center mt-1 space-x-3">
                          <div className="text-sm">
                            {capitalize(container.containerType || "")}
                          </div>
                          <div className="text-sm text-gray-500">
                            {container.length}" × {container.width}" ×{" "}
                            {container.height}"
                          </div>
                        </div>
                        <div className="mt-1 text-sm text-blue-600">
                          {container.containedInventories?.length || 0} pieces
                          inside
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500">
                        {container.client
                          ? container.client.companyName || "Unnamed Client"
                          : "No client"}
                      </div>
                      <div className="text-sm text-gray-500">
                        {container.location?.name}
                      </div>
                      <div className="text-xs mt-1">
                        <span
                          className={`px-2 py-1 rounded-full ${
                            container.status ===
                            InventoryStatusCode.AVAILABLE_ON_HAND
                              ? "bg-green-100 text-green-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {capitalize(container.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {expandedContainer === container.id && (
                  <div className="px-4 py-2 pb-4 bg-gray-50 border-t">
                    <div className="text-sm font-medium mb-2">Contents:</div>
                    <div className="space-y-2">
                      {container.containedInventories?.map((piece) => (
                        <div
                          key={piece.id}
                          className="flex justify-between items-center p-2 bg-white rounded border"
                        >
                          <div className="flex items-center space-x-2">
                            <Package size={14} className="text-gray-500" />
                            <div>
                              <div className="font-medium text-sm">
                                {piece.title}
                              </div>
                              <div className="text-xs text-gray-500">
                                {piece.inventoryRef}
                              </div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500">
                            {piece.length}" × {piece.width}" × {piece.height}"
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      className="w-full mt-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      onClick={() => {
                        setSelectedContainer(container);
                        goToNextStep();
                      }}
                    >
                      Select This Container
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="p-8 text-center">
              <Box size={40} className="mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700">
                No containers with contents found
              </h3>
              <p className="text-gray-500 mt-1">
                Select containers that have pieces inside them
              </p>
            </div>
          )}
          <div ref={observerRef} className="h-1"></div>
          {isFetchingNextPage && (
            <div className="p-4 text-center">
              <Spin />
            </div>
          )}
        </div>
      </Spin>
    </div>
  );
};
